import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from '../../useTranslation';
import AuthService from '../../services/AuthService';
import { AuthCardHeader } from './AuthCardHeader';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  inline?: boolean;
  language: string;
  onLanguageChange?: (language: string) => void;
  theme?: 'light' | 'dark';
  onThemeChange?: (theme: 'light' | 'dark') => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  isOpen,
  onClose,
  inline,
  language,
  onLanguageChange,
  theme,
  onThemeChange,
}) => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation(language);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await AuthService.requestPasswordReset(email, language);
      setSent(true);
    } catch (err: any) {
      setError(err.message || t.requestFailed);
    } finally {
      setIsLoading(false);
    }
  };

  const formCard = (
    <div
      className="p-5 sm:p-6 md:p-8 w-full max-w-md mx-auto relative overflow-hidden rounded-2xl border shadow-xl backdrop-blur-xl bg-white/95 dark:bg-slate-800/95 border-stone-200/80 dark:border-slate-600/80 text-stone-900 dark:text-slate-100"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-blue-400 rounded-t-2xl" />
      <AuthCardHeader
        language={language}
        onLanguageChange={onLanguageChange}
        theme={theme}
        onThemeChange={onThemeChange}
      />
      <h2 className="text-xl sm:text-2xl font-semibold text-stone-900 dark:text-white text-center">
        {t.forgotPasswordTitle ?? 'Password recovery'}
      </h2>

      {sent ? (
        <>
          <p className="text-stone-600 dark:text-slate-400 text-center mt-4 text-sm">
            {t.forgotPasswordSent}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="w-full mt-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium"
          >
            {t.close ?? 'Close'}
          </button>
        </>
      ) : (
        <>
          <p className="text-stone-500 dark:text-slate-400 text-center mt-1 mb-6 text-sm">
            {t.forgotPasswordHint}
          </p>
          {error && (
            <div className="bg-red-50/90 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-xl mb-4 text-sm">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 dark:text-slate-300 mb-1">
                {t.email ?? 'Email'}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-stone-200 dark:border-slate-500 bg-white/80 dark:bg-slate-700/50 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-400"
                placeholder="name@example.com"
                required
                autoComplete="email"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-xl min-h-[48px] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                t.resetPassword ?? 'Send reset link'
              )}
            </button>
          </form>
          <button type="button" onClick={onClose} className="w-full mt-4 text-sm text-stone-500 hover:text-stone-700 dark:hover:text-slate-300">
            {t.cancel ?? 'Cancel'}
          </button>
        </>
      )}
    </div>
  );

  if (inline) return isOpen ? formCard : null;
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-stone-900/40 dark:bg-black/50 backdrop-blur-md flex items-center justify-center z-[9999] p-4 sm:p-6">
      {formCard}
    </div>,
    document.body
  );
};

export default ForgotPasswordModal;
